import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { EtablissementSearchInput } from '@/components/schools/EtablissementSearchInput';
import { AcademieSelect } from '@/components/schools/AcademieSelect';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import type { AddressSuggestion, EtablissementSearchResult } from '@/api/schools';
import type { EstablishmentFormValues } from '@/components/schools/establishment-form';

interface EstablishmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Valeurs initiales à l'ouverture (édition) ou formulaire vide (création) */
  initialValues: EstablishmentFormValues;
  isEditing: boolean;
  isEducationNationale: boolean;
  /** Masque le switch « Établissement principal » (ex. brouillons secondaires du wizard) */
  allowPrincipalToggle?: boolean;
  /** Désactive le switch (l'établissement est déjà principal) */
  principalLocked?: boolean;
  saving?: boolean;
  /** Erreur serveur à afficher en plus de la validation locale */
  externalError?: string | null;
  onSubmit: (values: EstablishmentFormValues) => void;
}

export function EstablishmentFormDialog({
  open,
  onOpenChange,
  initialValues,
  isEditing,
  isEducationNationale,
  allowPrincipalToggle = true,
  principalLocked = false,
  saving = false,
  externalError = null,
  onSubmit,
}: EstablishmentFormDialogProps) {
  const [formData, setFormData] = useState<EstablishmentFormValues>(initialValues);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFormData(initialValues);
      setFormError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSelectAnnuaire = (result: EtablissementSearchResult) => {
    setFormData((prev) => ({
      ...prev,
      name: result.name || prev.name,
      rne: result.rne || prev.rne,
      codeAcademie: result.codeAcademie || prev.codeAcademie,
      address:
        [result.address, result.postalCode, result.city].filter(Boolean).join(' ') || prev.address,
      latitude: result.latitude ?? prev.latitude,
      longitude: result.longitude ?? prev.longitude,
    }));
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      setFormError('Le nom est requis');
      return;
    }
    if (formData.rne && !/^\d{7}[A-Z]$/.test(formData.rne)) {
      setFormError('Le RNE doit être au format UAI : 7 chiffres suivis d\'une lettre majuscule');
      return;
    }
    setFormError(null);
    onSubmit({ ...formData, name: formData.name.trim() });
  };

  const error = formError || externalError;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Modifier l\'établissement' : 'Ajouter un établissement'}
          </DialogTitle>
          <DialogDescription>
            Le RNE, l'académie et l'adresse sont propres à chaque établissement.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {isEducationNationale && (
            <div className="space-y-2">
              <Label>Rechercher dans l'annuaire Éducation Nationale</Label>
              <EtablissementSearchInput
                id="establishment-annuaire-search"
                onSelect={handleSelectAnnuaire}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="establishment-name">
              Nom <span className="text-red-500">*</span>
            </Label>
            <Input
              id="establishment-name"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Ex: Collège Saint-Joseph"
            />
          </div>

          {/* RNE et académie n'ont de sens que pour une école Éducation Nationale */}
          {isEducationNationale && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="establishment-rne">Code RNE (UAI)</Label>
                <Input
                  id="establishment-rne"
                  value={formData.rne}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, rne: e.target.value.toUpperCase() }))
                  }
                  placeholder="Ex: 0750001A"
                />
              </div>
              <div className="space-y-2">
                <Label>Académie</Label>
                <AcademieSelect
                  value={formData.codeAcademie}
                  onChange={(codeAcademie) => setFormData((prev) => ({ ...prev, codeAcademie }))}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="establishment-address">Adresse</Label>
            <AddressAutocomplete
              id="establishment-address"
              value={formData.address}
              country="FR"
              onChange={(address) => setFormData((prev) => ({ ...prev, address }))}
              onSelect={(suggestion: AddressSuggestion) => {
                setFormData((prev) => ({
                  ...prev,
                  address: suggestion.label,
                  latitude: suggestion.latitude,
                  longitude: suggestion.longitude,
                }));
              }}
              placeholder="12 rue de l'École, 75001 Paris"
            />
            <Input
              value={formData.addressComplement}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, addressComplement: e.target.value }))
              }
              placeholder="Complément d'adresse"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div className="space-y-2">
              <Label>Type de contrat</Label>
              <Select
                value={formData.contractType || 'none'}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    contractType:
                      value === 'none' ? '' : (value as 'with_contract' | 'without_contract'),
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non renseigné</SelectItem>
                  <SelectItem value="with_contract">Sous contrat</SelectItem>
                  <SelectItem value="without_contract">Hors contrat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {allowPrincipalToggle && (
              <div className="flex items-center gap-2 pb-2">
                <Switch
                  id="establishment-principal"
                  checked={formData.isPrincipal}
                  disabled={principalLocked}
                  onCheckedChange={(isPrincipal) =>
                    setFormData((prev) => ({ ...prev, isPrincipal }))
                  }
                />
                <Label htmlFor="establishment-principal" className="cursor-pointer">
                  Établissement principal
                </Label>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEditing ? 'Enregistrer' : 'Ajouter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
